import React, { useState } from 'react';
import { Constants } from "@eagerdog/constants";
import { apiService } from "src/services";

import { toast } from "src/components/Toast/ToastManager";
import { Modal, useModal } from "src/components/Modal/Modal";
import Confirm from "src/components/Confirm/Confirm";
import ContextMenu, { IContextMenuOption } from "src/components/ContextMenu/ContextMenu";
import MoveUp from "src/components/MoveUp/MoveUp";
import EnterResults from "src/components/EnterResults/EnterResults";

import type { CustomCellRendererProps } from 'ag-grid-react';

import styles from "./ResultsTab.module.scss";
import { IEventMessage, IEventSummary, IShow } from "@eagerdog/interfaces";
import SendMessage, { SendMessageType } from "../../../components/SendMessage/SendMessage";
import { download } from "../../../components/Download/DownloadManager";

interface CustomResultsCellRendererProps extends CustomCellRendererProps {
  getResults(): void
  event: IEventSummary
  shows?: IShow[]
}

enum ActionsRendererMenuOptions {
  Move = "move",
  Enter = "enter",
  Email = "email",
}

export const ActionsRenderer = (props: CustomResultsCellRendererProps) => {
  const [modalContent, setModalContent] = useState<ActionsRendererMenuOptions | undefined>();
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);

  const { isShown, toggle } = useModal();

  let noMoveUp:string[] = [
    Constants.show_type.fast_cat
  ];

  const getShowData = () => {
    if (props.shows && props.value.showId) {
      return props.shows.find(show => show._id === props.value.showId);
    }
    return undefined;
  };

  const emailResults = (payload: IEventMessage) => {
    setIsSending(true)
    const content = payload.is_test_sending
      ? `Test email sent to ${props.value.event.club.email}.`
      : "You've successfully emailed the individual results"

    apiService.requestResultsEmailForShowRegistration(props.value.event._id, props.value.result._id, payload).then((response) => {
      toast.show({
        title: "Email Result",
        content,
        duration: 10000,
        type: "success"
      });
    }).catch((e) => {
      toast.show({
        title: "Reset Password",
        content: e.response.data.message ? "There is no account with that email" : "Something went wrong, please try again later",
        duration: 10000,
        type: "fail"
      });
    }).finally(() => {
      setIsSending(false)
      toggle();
      setModalContent(undefined);
    });
  }

  let menuOptions:IContextMenuOption[] = [];
  if (!noMoveUp.includes(props.data.element)) {
    menuOptions.push({
      label: "Move Up",
      onClick:() => {
        setModalContent(ActionsRendererMenuOptions.Move);
        toggle();
      }
    });
  }

  if (props.value.canEnterResults) {
    menuOptions.push({
      label: "Enter Results",
      onClick:() => {
        setModalContent(ActionsRendererMenuOptions.Enter);
        toggle();
      }
    });

    if (props.data.result !== "unknown") {
      menuOptions.push({
        label: "Clear Results",
        onClick: () => setShowConfirm(true)
      });
    }

    menuOptions.push({
      label: "Email Results",
      onClick:() => {
        setModalContent(ActionsRendererMenuOptions.Email);
        toggle();
      }
    });
  }

  if (props.value.canDownloadIndividualResults) {
    menuOptions.push({
      label: "Download Scoresheet",
      onClick:() => {
        let _downloadOptions: any = {
          type: "scoresheets",
          eventId: props.value.event._id,
          showId: props.value.showId,
          scoreId: props.value.result._id
        };
        download.pushDownload(_downloadOptions);
      }
    });
  }

  const getModalClassName = () => {
    switch(modalContent) {
      case ActionsRendererMenuOptions.Move:
        return "moveUpModal";
      case ActionsRendererMenuOptions.Enter:
        return "enterResultsModal";
      case ActionsRendererMenuOptions.Email:
        return "sendMessageModal";
    }

    return "";
  }

  const getModalContent = () => {
    switch(modalContent) {
      case ActionsRendererMenuOptions.Move:
        return <MoveUp
            event={props.value.event}
            registration={props.value.result}
            onComplete={() => {
                toggle();
                setModalContent(undefined);
                props.getResults();
              }
            }
            onClose={() => {
                toggle();
                setModalContent(undefined);
                props.getResults();
              }
            }
          />;
      case ActionsRendererMenuOptions.Enter:
        return <EnterResults
          event={props.value.event}
          eventId={props.value.event._id}
          showType={props.value.result.show_type}
          result={props.value.result}
          show={getShowData()}
          onSave={() => {
            toggle();
            setModalContent(undefined);
            props.getResults();
          }}
          onClose={() => {
            toggle();
            setModalContent(undefined);
            props.getResults();
          }}
        />;
      case ActionsRendererMenuOptions.Email:
        const eventDate = new Intl.DateTimeFormat("en-US", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          timeZone: props.event.timezone || "America/Toronto",
        }).format(new Date(props.event.start_date))

        return <SendMessage
          isSending={isSending}
          handleSendMessage={emailResults}
          subject={`${props.event.title} Results - ${eventDate}`}
          messageBody={`
          <p>Congratulations on completing another great event!</p>
          <p>Please find your results attached for all dogs you competed with.</p>
          <p>${props.event.club.club_name} - ${props.event.club.email} - ${props.event.club.phone}</p>
        `}
          type={SendMessageType.EmailResults}
        />
    }

    return <></>;
  }

  const handleClearResult = () => {
    apiService.deleteScoreResult(props.value.event._id, props.value.result._id)
      .then(() => {
        props.getResults();
        toast.show({
          title: "Clear Result",
          content: "You've successfully cleared the individual results",
          duration: 10000,
          type: "success"
        });
      })
      .catch((error) => {
        toast.show({
          title: "Clear Result",
          content: error.response.data.message ??  "Something went wrong, please try again later",
          duration: 10000,
          type: "fail"
        });
      })
      .finally(() => setShowConfirm(false));
  }

  return (<div className={styles.ActionRenderer}>
    <ContextMenu
      options={menuOptions}
    />
    <Modal
      className={getModalClassName()}
      isShown={isShown}
      hide={toggle}
      modalContent={getModalContent()}
    />
    <Modal className="Confirmation" isShown={showConfirm} hide={() => { setShowConfirm(false); }}
      modalContent={
        <Confirm
          message={<><p>Are you sure you want to clear this result?</p></>}
          onConfirm={handleClearResult}
          onCancel={() => { setShowConfirm(false); }}
        />
      }
    />
  </div>);
};